import { Injectable } from '@angular/core'

import { Actions, Effect, ofType } from '@ngrx/effects'
import { Observable, of } from 'rxjs'
import { Action, Store, select } from '@ngrx/store'
import { withLatestFrom, switchMap, map, catchError } from 'rxjs/operators'

import { OrderHistoryService } from '../services/orderhistory.service'
import { ProductService } from '../../catalog/services/product-service'

import * as orderhistory from '../actions/order-history.actions'

import * as fromRoot from '../../reducers'
import * as fromAuth from '../../auth/reducers'
import * as fromOrderHistory from '../reducers'

@Injectable()
export class OrderHistoryEffects {
  constructor(
    private actions$: Actions,
    private store: Store<fromRoot.State>,
    private orderHistorySvc: OrderHistoryService,
    private productsSvc: ProductService,
  ) { }

  @Effect()
  loadOrderHistory$: Observable<Action> =
    this.actions$.ofType(orderhistory.OrderHistoryActionTypes.LoadOrderHistory)
      .pipe(
        withLatestFrom(
          this.store.pipe(select(fromAuth.getCustomerId)),
          (action, customerId) => ({ customerId })
        ),
        switchMap((data) => {
          return this.orderHistorySvc
            .getOrderHeaders(data.customerId)
            .pipe(
              map(response => new orderhistory.LoadOrderHistorySuccess(response)),
              catchError(e => of(new orderhistory.LoadOrderHistoryFailed(e)))
            )
        })
      )

  @Effect()
  expandOrderHistory$: Observable<Action> =
    this.actions$.ofType(orderhistory.OrderHistoryActionTypes.LoadOrderHistoryExpand)
        .pipe(
          withLatestFrom(
            this.store.pipe(select(fromAuth.getCustomerId)),
            this.store.pipe(select(fromOrderHistory.getOrderHistoryNextPage)),
            (action, customerId, nextPage) => ({ customerId, nextPage })
          ),
          switchMap((data) => {
            return this.orderHistorySvc
              .getOrderHeaders(data.customerId, null, data.nextPage)
              .pipe(
                map(response => new orderhistory.LoadOrderHistoryExpandSuccess(response)),
                catchError(e => of(new orderhistory.LoadOrderHistoryExpandFailed(e)))
              )
          })
        )

  @Effect()
  loadOrderHistoryLines$: Observable<Action> =
    this.actions$.ofType<orderhistory.LoadOrderHistoryLines>(orderhistory.OrderHistoryActionTypes.LoadOrderHistoryLines)
          .pipe(
            withLatestFrom(
              this.store.pipe(select(fromAuth.getCustomerId)),
              this.store.pipe(select(fromOrderHistory.getSelectedOrdernumber)),
              (action, customerId, ordernumber) => ({ customerId, ordernumber })
            ),
            switchMap((data) => {
              if (isNaN(data.ordernumber)) {
                return of(new orderhistory.LoadOrderHistoryLinesFailed('Invalid ordernumber'))
              }

              return this.orderHistorySvc
                .getOrderLines(data.customerId, data.ordernumber)
                .pipe(
                  map(response => new orderhistory.LoadOrderHistoryLinesSuccess(response)),
                  catchError(e => of(new orderhistory.LoadOrderHistoryLinesFailed(e)))
                )
            })
          )

  @Effect()
  loadOrderHistoryLinesExpand$: Observable<Action> =
    this.actions$.ofType(orderhistory.OrderHistoryActionTypes.LoadOrderHistoryLinesExpand)
      .pipe(
        withLatestFrom(
          this.store.pipe(select(fromAuth.getCustomerId)),
          this.store.pipe(select(fromOrderHistory.getSelectedOrdernumber)),
          this.store.pipe(select(fromOrderHistory.getOrderHistoryLinesNextPage)),
          (action, customerId, ordernumber, nextPage) => ({ customerId, ordernumber, nextPage })
        ),
        switchMap((data) => {
          return this.orderHistorySvc
            .getOrderLines(data.customerId, data.ordernumber, data.nextPage)
            .pipe(
              map(response => new orderhistory.LoadOrderHistoryLinesExpandSuccess(response)),
              catchError(e => of(new orderhistory.LoadOrderHistoryLinesExpandFailed(e)))
            )
        })
      )

  @Effect()
  loadOrderHistoryItem$: Observable<Action> =
    this.actions$.ofType(orderhistory.OrderHistoryActionTypes.LoadOrderHistoryItem)
      .pipe(
        withLatestFrom(
          this.store.pipe(select(fromAuth.getCustomerId)),
          this.store.pipe(select(fromOrderHistory.getSelectedOrdernumber)),
          (action, customerId, ordernumber) => ({ customerId, ordernumber })
        ),
      switchMap((data) => {
        return this.orderHistorySvc
          .getOrderHeader(data.customerId, data.ordernumber)
          .pipe(
            map(response => new orderhistory.LoadOrderHistoryItemSuccess(response)),
            catchError(e => of(new orderhistory.LoadOrderHistoryItemFailed(e)))
          )
      })
    )

  @Effect()
  loadReOrderableItems$: Observable<Action> =
      this.actions$.pipe(
        ofType(orderhistory.OrderHistoryActionTypes.LoadReOrderableItems),
        withLatestFrom(
          this.store.pipe(select(fromAuth.getCustomerId)),
          this.store.pipe(select(fromOrderHistory.getSelectedOrdernumber)),
          (action, customerId, ordernumber) => ({ customerId, ordernumber })
        ),
        switchMap((data) => {
          return this.productsSvc.findProducts(data.customerId, null, null, null, null, null, null, `order_${data.ordernumber}`, true)
            .pipe(
              map((response) => new orderhistory.LoadReOrderableItemsSuccess(response)),
              catchError((e) => of(new orderhistory.LoadReOrderableItemsFailed(e)))
            )
        })
      )
}
